import {Component, inject} from '@angular/core';
import {UIStateEnum} from "../../enum/UIStateEnum";
import {FormBuilder, FormControl, ReactiveFormsModule, Validators} from "@angular/forms";
import {IApiErrorResponse} from "../../interfaces/error/IApiErrorResponse";
import {UsersService} from "../../services/users.service";
import {ISendUpdateEmailAddressEmail} from "../../interfaces/ISendUpdateEmailAddressEmail";
import {SnackbarService} from "../../services/snackbar.service";
import {ActiveUserService} from "../../services/active-user.service";
import {ErrorHandlingService} from "../../services/error-handling.service";
import {MatCardModule} from "@angular/material/card";
import {MatFormFieldModule, MatLabel} from "@angular/material/form-field";
import {MatInput} from "@angular/material/input";
import {MatIcon} from "@angular/material/icon";
import {MatButton} from "@angular/material/button";
import {ReCaptchaV3Service, RecaptchaV3Module} from "ng-recaptcha";
import {switchMap} from "rxjs";

// Mirrors the relevant values in Causable.Api.Data.Enum.ErrorTypes (there is no shared front-end error-type enum).
const UPDATE_EMAIL_ERROR = {
  CaptchaFailed: 463,
  EmailAlreadyInUse: 464,
  PasswordDoesntMatch: 466
} as const;

@Component({
  selector: 'app-update-email-address',
  standalone: true,
  templateUrl: './update-email-address.component.html',
  imports: [
    MatCardModule,
    ReactiveFormsModule,
    MatFormFieldModule,
    MatIcon,
    MatLabel,
    MatButton,
    MatInput,
    RecaptchaV3Module
  ],
  styleUrls: ['./update-email-address.component.scss']
})
export class UpdateEmailAddressComponent {

  private formBuilder: FormBuilder = inject(FormBuilder);
  private userService: UsersService = inject(UsersService);
  private errorHandlingService: ErrorHandlingService = inject(ErrorHandlingService);
  private snackBarService: SnackbarService = inject(SnackbarService);
  private activeUserService: ActiveUserService = inject(ActiveUserService);
  private recaptchaV3Service: ReCaptchaV3Service = inject(ReCaptchaV3Service);

  public uiStateEnum: UIStateEnum = UIStateEnum.ShowData;
  public uiStateEnumForTemplate = UIStateEnum;
  public showPassword: boolean = false;
  public showConfirmationCompleteMessage: boolean = false;
  public activeAdmin = this.activeUserService.activeUser();
  public newEmailControl: FormControl = new FormControl<string>('', [
    Validators.required,
    Validators.email
  ]);
  public passwordControl: FormControl = new FormControl<string>('', Validators.required);

  public updateEmailForm = this.formBuilder.group({
    password: this.passwordControl,
    newEmail: this.newEmailControl
  })

  updateEmailAddress() {

    if (!this.updateEmailForm.valid) {
      this.updateEmailForm.markAllAsTouched()
      return;
    }

    if (this.activeAdmin.Id) {
      this.updateEmailForm.disable();
      this.uiStateEnum = UIStateEnum.ShowLoading;

      this.recaptchaV3Service.execute('update_admin_email').pipe(
        switchMap((token) => {
          const sendUpdateEmailEmail: ISendUpdateEmailAddressEmail = {
            password: this.passwordControl.value,
            newEmail: this.newEmailControl.value,
            adminId: this.activeAdmin.Id,
            oldEmail: this.activeAdmin.Email,
            redirectUrl: `${window.location.origin}/login`,
            captcha: token
          };

          return this.userService.sendUpdateAdminEmailAddressEmail(sendUpdateEmailEmail);
        })
      ).subscribe({
        next: () => {
          this.uiStateEnum = UIStateEnum.ShowData;
          this.showConfirmationCompleteMessage = true;
          this.snackBarService.openSuccessfulSnackBar('Email updated successfully!');
        },
        error: (res: IApiErrorResponse) => {
          this.updateEmailForm.enable();
          this.uiStateEnum = UIStateEnum.ShowData;
          this.errorHandlingService.displayPageLevelErrorMessage(res, this.resolveUpdateEmailErrorMessage(res));
        }
      });
    }
  }

  // Turns a failed update-email response into specific, actionable copy.
  private resolveUpdateEmailErrorMessage(res: IApiErrorResponse): string {
    if (res.Error?.ClientErrorMessages?.length > 0) {
      return res.Error.ClientErrorMessages.toString();
    }

    switch (res.Error?.ErrorType) {
      case UPDATE_EMAIL_ERROR.EmailAlreadyInUse:
        return "Looks like this email address is already being used. Please try again with an address that isn't being used.";
      case UPDATE_EMAIL_ERROR.PasswordDoesntMatch:
        return 'Looks like your old password did not match. Please try again.';
      case UPDATE_EMAIL_ERROR.CaptchaFailed:
        return "We couldn't verify your request. Please refresh and try again.";
      default:
        return '';
    }
  }
}
